/* eslint-disable @typescript-eslint/no-empty-function */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { Staff } from '@wsphere/staff/domain';
import { BehaviorSubject, combineLatest, map, shareReplay, Subscription, switchMap } from 'rxjs';
import { SecurityService } from '../../services';

@Component({
  selector: 'ws-role-picker',
  templateUrl: './role-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: RolePickerComponent,
    },
  ],
})
export class RolePickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
  constructor(
    public elementRef: ElementRef,
    private securitySvc: SecurityService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.availableRolesSub = this.availableRoles$.subscribe(roles => {
      this.rolePermissions = new Map(roles.map(role => [role.id, Staff.Security.getPermissions(role.permissions)]));
      this.cdr.markForCheck();
    });
  }

  @HostBinding('class') _class = 'block';

  @Input() filter = false;
  @Input() required = false;
  @Input() disabled = false;
  /** Set false to hide the Full Administrator option (e.g. role bundles must never grant the wildcard). */
  @Input() allowFullAdmin = true;
  @Input() placeholder = 'Search roles';
  @Input() themeColor: ThemeColor = 'primary';

  @Input() rolesOutside = false;

  touched = false;

  @PropertyListener('permissionKey') permissionKey$ = new BehaviorSubject<string | null>(null);
  @Input() permissionKey: string | null = null;

  @PropertyListener('value') value$ = new BehaviorSubject<string[]>([]);
  @Input() value: string[] = [];

  @Output() roleAdded = new EventEmitter<string>();
  @Output() roleRemoved = new EventEmitter<string>();

  @Output() openChanged = new EventEmitter<boolean>();
  @Output() valueChanges = new EventEmitter<string[]>();

  readonly searchQuery$ = new BehaviorSubject<string>('');

  fullAdmin = '*';
  showAdmin = false;
  overlayOpen = false;

  selectedGroup: { group: string; roles: Staff.Security.Role[] } | null = null;

  // Roles the current staff can grant. A 403 just means an empty add list - existing badges stay removable.
  private availableRoles$ = this.permissionKey$.pipe(
    switchMap(permissionKey =>
      this.securitySvc.getAvailableRoles(permissionKey).catch(() => [] as Staff.Security.Role[])
    ),
    map(roles => roles.filter(role => !role.hideRole)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  groupsAndRoles$ = combineLatest([this.searchQuery$, this.value$, this.availableRoles$]).pipe(
    map(([search, value, availableRoles]) => {
      this.showAdmin = this.allowFullAdmin && availableRoles.some(role => role.id === this.fullAdmin);

      const unselectedRoles = availableRoles.filter(role => !value.some(roleId => role.id === roleId));
      const filtered = !search
        ? unselectedRoles
        : unselectedRoles.filter(role => role.display.title.toLowerCase().includes(search.toLowerCase()));

      const groups = filtered.reduce((acc, role) => {
        if (!acc.includes(role.group)) acc.push(role.group);
        return acc;
      }, [] as string[]);

      const groupsAndRoles: { group: string; roles: Staff.Security.Role[] }[] = [];
      groups.forEach(group => {
        const groupRoles = filtered.filter(role => role.group === group && role.group !== 'Platform Management');
        if (groupRoles.length) groupsAndRoles.push({ group, roles: groupRoles });
      });

      return groupsAndRoles;
    })
  );

  // Per-role permissions for the badge tooltips.
  private rolePermissions = new Map<string, Staff.Security.Permission[]>();
  private availableRolesSub?: Subscription;

  permissionsFor(roleId: string) {
    return this.rolePermissions.get(roleId);
  }

  onChange = (_value: string[]) => {};
  onTouched = () => {};

  writeValue(value: string[]): void {
    this.value = value || [];
  }

  registerOnChange(onChange: (_value: string[]) => {}): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => {}): void {
    this.onTouched = onTouched;
  }

  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  open() {
    this.overlayOpen = true;
  }

  close() {
    this.overlayOpen = false;
  }

  toggle() {
    this.overlayOpen = !this.overlayOpen;
  }

  hasAllPermissions(roles: Staff.Security.Role[]) {
    let hasAll = true;

    roles.forEach(role => {
      if (hasAll && !this.value.includes(role.id)) hasAll = false;
    });

    return hasAll;
  }

  selectAll(roles: Staff.Security.Role[]) {
    roles.forEach(role => {
      if (!this.value.includes(role.id)) this.addRole(role.id);
    });
  }

  removeAll(roles: Staff.Security.Role[]) {
    roles.forEach(role => {
      if (this.value.includes(role.id)) this.removeRole(role.id);
    });
  }

  addRole(roleId: string) {
    // No-op while disabled, and a double-click can't emit the same change twice
    if (this.disabled || this.value.includes(roleId)) return;
    this.markAsTouched();
    this.value.push(roleId);
    this.onChange(this.value);
    this.valueChanges.next(this.value);
    this.roleAdded.emit(roleId);
  }

  removeRole(roleId: string) {
    if (this.disabled || !this.value.includes(roleId)) return;
    this.value = this.value.filter(value => value !== roleId);
    this.onChange(this.value);
    this.valueChanges.next(this.value);
    this.roleRemoved.emit(roleId);
  }

  ngOnDestroy(): void {
    this.availableRolesSub?.unsubscribe();
    this.value$.complete();
    this.searchQuery$.complete();
  }
}
